import { useFormContext, useFormState } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import FormCheckbox from "../../shared/FormCheckbox";
import FormInputText from "../../shared/FormInputText";
import FormSelect from "../../shared/FormSelect";
import { getBillingCycles } from "../../../utils/apiUtils";
import { SUB_ACCOUNT_DRAFT, USA_STATES } from "../../../utils/constants";

const step = 3;
const SubAccountStepThree = ({ activeStep }) => {
    const { control, register, setValue, getValues, resetField, trigger } = useFormContext();
    const { errors } = useFormState({ control });

    const { data: billingCycles } = useQuery({
        queryKey: ['getBillingCycles'],
        queryFn: () => getBillingCycles(),
        select: (data) => data?.rows.map((entry) => {
            return { value: entry.billingCycleFactKey, label: entry.billingCycleName }})
    });

    if (activeStep !== step) return null;

    const parentAccountId = getValues(`${SUB_ACCOUNT_DRAFT}.parentAccount`)?.key;

    const onSelectedBillParentCheckbox = async (checked) => {
        if (checked && parentAccountId) {
            const topAccount = getValues();
            const subAccounts = topAccount.subAccounts ?? [];
            const parentAccount = [topAccount, ...subAccounts].find(a => a.accountId === parentAccountId);

            setValue(`${SUB_ACCOUNT_DRAFT}.billingCycle`, parentAccount.billingCycle, { shouldValidate: true });
            setValue(`${SUB_ACCOUNT_DRAFT}.ignoreBillingAddressCheck`, parentAccount.ignoreBillingAddressCheck);
            setValue(`${SUB_ACCOUNT_DRAFT}.billingAddress1`, parentAccount.billingAddress1 ?? '', { shouldValidate: true });
            setValue(`${SUB_ACCOUNT_DRAFT}.billingAddress2`, parentAccount.billingAddress2 ?? '');
            setValue(`${SUB_ACCOUNT_DRAFT}.billingAddress3`, parentAccount.billingAddress3 ?? '');
            setValue(`${SUB_ACCOUNT_DRAFT}.billingAddressCity`, parentAccount.billingAddressCity ?? '', { shouldValidate: true });
            setValue(`${SUB_ACCOUNT_DRAFT}.billingAddressState`, parentAccount.billingAddressState, { shouldValidate: true });
            setValue(`${SUB_ACCOUNT_DRAFT}.billingAddressZip`, parentAccount.billingAddressZip ?? '', { shouldValidate: true });
        } else {
            resetField(`${SUB_ACCOUNT_DRAFT}.billingCycle`);
            resetField(`${SUB_ACCOUNT_DRAFT}.ignoreBillingAddressCheck`);
            resetField(`${SUB_ACCOUNT_DRAFT}.billingAddress1`);
            resetField(`${SUB_ACCOUNT_DRAFT}.billingAddress2`);
            resetField(`${SUB_ACCOUNT_DRAFT}.billingAddress3`);
            resetField(`${SUB_ACCOUNT_DRAFT}.billingAddressCity`);
            resetField(`${SUB_ACCOUNT_DRAFT}.billingAddressState`);
            resetField(`${SUB_ACCOUNT_DRAFT}.billingAddressZip`);

            await trigger([
                `${SUB_ACCOUNT_DRAFT}.billingCycle`,
                `${SUB_ACCOUNT_DRAFT}.billingAddress1`,
                `${SUB_ACCOUNT_DRAFT}.billingAddressCity`,
                `${SUB_ACCOUNT_DRAFT}.billingAddressState`,
                `${SUB_ACCOUNT_DRAFT}.billingAddressZip`
            ]);
        }
    };

    const onSelectedCopyCheckbox = async (checked) => {
        if (checked) {
            const draft = getValues(SUB_ACCOUNT_DRAFT);
            setValue(`${SUB_ACCOUNT_DRAFT}.ignoreBillingAddressCheck`, draft.ignorePrimaryAddressCheck);
            setValue(`${SUB_ACCOUNT_DRAFT}.billingAddress1`, draft.primaryAddress1 ?? '', { shouldValidate: true });
            setValue(`${SUB_ACCOUNT_DRAFT}.billingAddress2`, draft.primaryAddress2 ?? '');
            setValue(`${SUB_ACCOUNT_DRAFT}.billingAddress3`, draft.primaryAddress3 ?? '');
            setValue(`${SUB_ACCOUNT_DRAFT}.billingAddressCity`, draft.primaryAddressCity ?? '', { shouldValidate: true });
            setValue(`${SUB_ACCOUNT_DRAFT}.billingAddressState`, draft.primaryAddressState, { shouldValidate: true });
            setValue(`${SUB_ACCOUNT_DRAFT}.billingAddressZip`, draft.primaryAddressZip ?? '', { shouldValidate: true });
        } else {
            resetField(`${SUB_ACCOUNT_DRAFT}.ignoreBillingAddressCheck`);
            resetField(`${SUB_ACCOUNT_DRAFT}.billingAddress1`);
            resetField(`${SUB_ACCOUNT_DRAFT}.billingAddress2`);
            resetField(`${SUB_ACCOUNT_DRAFT}.billingAddress3`);
            resetField(`${SUB_ACCOUNT_DRAFT}.billingAddressCity`);
            resetField(`${SUB_ACCOUNT_DRAFT}.billingAddressState`);
            resetField(`${SUB_ACCOUNT_DRAFT}.billingAddressZip`);

            await trigger([
                `${SUB_ACCOUNT_DRAFT}.billingAddress1`,
                `${SUB_ACCOUNT_DRAFT}.billingAddressCity`,
                `${SUB_ACCOUNT_DRAFT}.billingAddressState`,
                `${SUB_ACCOUNT_DRAFT}.billingAddressZip`
            ]);
        }
    }

    return (
        <div style={{ width: "50%" }}>
            <FormCheckbox
                label={'Bill Parent'}
                name={`${SUB_ACCOUNT_DRAFT}.billParent`}
                control={control}
                disabled={!parentAccountId}
                onSelectedHandler={onSelectedBillParentCheckbox}
            />
            <br />
            {!getValues(`${SUB_ACCOUNT_DRAFT}.billParent`) && (
                <>
                    <FormSelect
                        label={'Billing Cycle Name'}
                        name={`${SUB_ACCOUNT_DRAFT}.billingCycle`}
                        control={control}
                        options={billingCycles}
                        errors={errors}
                        placeholder="Name"
                    />
                    <FormCheckbox
                        label={'Copy Primary Correspondence Address'}
                        name={`${SUB_ACCOUNT_DRAFT}.copyPrimaryCorrespondence`}
                        control={control}
                        onSelectedHandler={onSelectedCopyCheckbox}
                    />
                    <FormCheckbox
                        label={'Ignore Address Check'}
                        name={`${SUB_ACCOUNT_DRAFT}.ignoreBillingAddressCheck`}
                        control={control}
                    />
                    <br />
                    <FormInputText label={'Premium Billing - Address Line 1'} register={register} name={`${SUB_ACCOUNT_DRAFT}.billingAddress1`} errors={errors} placeholder="Address" />
                    <FormInputText label={'Premium Billing - Address Line 2'} register={register} name={`${SUB_ACCOUNT_DRAFT}.billingAddress2`} errors={errors} placeholder="Address (optional)" />
                    <FormInputText label={'Premium Billing - Address Line 3'} register={register} name={`${SUB_ACCOUNT_DRAFT}.billingAddress3`} errors={errors} placeholder="Address (optional)" />
                    <FormInputText label={'Premium Billing - Address City'} register={register} name={`${SUB_ACCOUNT_DRAFT}.billingAddressCity`} errors={errors} placeholder="City" />
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: '2rem' }}>
                        <FormSelect
                            label={'Premium Billing - Address State'}
                            name={`${SUB_ACCOUNT_DRAFT}.billingAddressState`}
                            control={control}
                            options={USA_STATES}
                            errors={errors}
                            placeholder="Select a state"
                        />
                        <FormInputText label={'Premium Billing - Address Zip'} register={register} name={`${SUB_ACCOUNT_DRAFT}.billingAddressZip`} errors={errors} placeholder="Zip code" />
                    </div>    
                </>
            )}
        </div>
    );
};

export default SubAccountStepThree;
